import { QueryClient } from "react-query";
import { getCreditCardAccount } from "./getCreditCardAccount";
import { getMinimalAccountsInfo } from "./getMinimalAccountsInfo";
import { getPLAccount } from "./getPLAccount";
import { getSelfHref } from "./getSelfHref";
import { Account } from "./types";

interface QueryFullInfoProps {
  queryClient: QueryClient;
  accounts: Account[];
  getAccountById: (accountId: string) => Promise<Account>;
}
export const queryFullInfo = async ({
  queryClient,
  accounts,
  getAccountById,
}: QueryFullInfoProps): Promise<Account[]> => {
  const promises = accounts.map((account) => {
    const href = getSelfHref(account);

    if (href) {
      return queryClient
        .fetchQuery(
          ["account", { accountId: account.account_id }],
          () => getAccountById(account.account_id),
          {},
        )
        .then((accountInfoFull) => ({
          ...accountInfoFull,
          status: account.status,
        }))
        .catch((error) => {
          console.error(error);
          return Promise.resolve(account);
        });
    }

    return Promise.resolve(account);
  });

  const result = await Promise.all(promises);

  return result.filter(Boolean) as Account[];
};

interface Params {
  queryClient: QueryClient;
  fullCreditCardsInfo: boolean;
  fullPersonalLoansInfo: boolean;
}

export async function getFullAccountsInfo({
  queryClient,
  fullCreditCardsInfo,
  fullPersonalLoansInfo,
}: Params): Promise<{
  credit_cards: Account[];
  personal_loans: Account[];
}> {
  const accounts: {
    credit_cards: Account[];
    personal_loans: Account[];
  } = await queryClient.fetchQuery(
    ["accounts", { fullCreditCardsInfo: false, fullPersonalLoansInfo: false }],
    getMinimalAccountsInfo,
  );

  const result = {
    credit_cards: accounts.credit_cards,
    personal_loans: accounts.personal_loans,
  };

  if (fullCreditCardsInfo) {
    result.credit_cards = await queryFullInfo({
      queryClient,
      accounts: accounts.credit_cards,
      getAccountById: getCreditCardAccount,
    });
  }

  if (fullPersonalLoansInfo) {
    result.personal_loans = await queryFullInfo({
      queryClient,
      accounts: accounts.personal_loans,
      getAccountById: getPLAccount,
    });
  }

  return result;
}
